import {
  PROJECT_FILE_PIPELINE_AI_STEPS,
  type ProjectFilePipelineAiStep,
} from '../models/ai/project-file-pipeline-ai.model';
import type { ProjectFileAiCall } from '../models/project-file-ai-call.model';
import type {
  ProjectFileClassificationEnvelope,
  ProjectFileClassificationType,
  ProjectFileDocumentKind,
} from '../models/project-file-batch.model';

/** Segment types whose extraction reads the drawing itself, not just text. */
const VISUAL_SEGMENT_TYPES: Record<ProjectFileClassificationType, boolean> = {
  blueprint: true,
  building_rendering: true,
  title_block: true,
  project_description: false,
  project_brief: false,
  area_schedule: false,
  bill_of_materials: false,
  cost_estimate: false,
  technical_spec: false,
  unknown: false,
};

/**
 * Whole-file kinds that can carry drawn content (CAD area tables, sheet
 * registers, bundles). Text segments in these files still get the drawing
 * step so tables drawn as vectors stay legible.
 */
const VISUAL_DOCUMENT_KINDS: Record<ProjectFileDocumentKind, boolean> = {
  technical_drawing_set: true,
  cover_title_register: true,
  area_room_schedule: true,
  visualization_rendering: true,
  site_plan_landscape: true,
  photo_image_survey: true,
  mixed_project_bundle: true,
  unknown_other: true,
  written_technical_spec: false,
  project_description_prose: false,
  bill_of_quantities: false,
  cost_estimate_budget: false,
  spreadsheet_table_data: false,
  contract_legal: false,
  energy_sustainability_report: false,
};

/**
 * Picks the extraction step for one classifier segment. Only a text segment
 * in a text document takes the text step; an unknown document kind stays
 * on the drawing step.
 */
export const projectFileSegmentAiStep = (
  type: ProjectFileClassificationType,
  documentKind: ProjectFileDocumentKind | null,
): Extract<
  ProjectFilePipelineAiStep,
  'drawingSegmentExtraction' | 'textSegmentExtraction'
> => {
  if (VISUAL_SEGMENT_TYPES[type]) return 'drawingSegmentExtraction';
  if (documentKind === null || VISUAL_DOCUMENT_KINDS[documentKind]) {
    return 'drawingSegmentExtraction';
  }
  return 'textSegmentExtraction';
};

/**
 * Whole-file kinds whose pages are measured drawings even without a
 * `blueprint` segment: a drawing set, or a site plan that shows the
 * buildings in one frame.
 */
const DRAWING_DOCUMENT_KINDS: Record<ProjectFileDocumentKind, boolean> = {
  technical_drawing_set: true,
  site_plan_landscape: true,
  cover_title_register: false,
  area_room_schedule: false,
  visualization_rendering: false,
  photo_image_survey: false,
  mixed_project_bundle: false,
  unknown_other: false,
  written_technical_spec: false,
  project_description_prose: false,
  bill_of_quantities: false,
  cost_estimate_budget: false,
  spreadsheet_table_data: false,
  contract_legal: false,
  energy_sustainability_report: false,
};

/**
 * Drawing detection runs on files the classifier saw drawings in: a
 * `blueprint` segment, or a whole file classified as a drawing set or a
 * site plan ({@link DRAWING_DOCUMENT_KINDS}).
 */
export const shouldDetectProjectFileDrawings = (input: {
  classifications: readonly ProjectFileClassificationEnvelope[];
  documentKind: ProjectFileDocumentKind | null;
}): boolean =>
  (input.documentKind !== null && DRAWING_DOCUMENT_KINDS[input.documentKind]) ||
  input.classifications.some((env) => env.data.type === 'blueprint');

export type ProjectFileAiStepTotals = {
  calls: number;
  failedCalls: number;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  durationMs: number;
};

const emptyTotals = (): ProjectFileAiStepTotals => ({
  calls: 0,
  failedCalls: 0,
  inputTokens: 0,
  cachedInputTokens: 0,
  cacheWriteTokens: 0,
  outputTokens: 0,
  reasoningTokens: 0,
  durationMs: 0,
});

const addCall = (
  totals: ProjectFileAiStepTotals,
  call: ProjectFileAiCall,
): ProjectFileAiStepTotals => {
  const usage = call.outcome === 'ok' ? call.usage : null;
  return {
    calls: totals.calls + 1,
    failedCalls: totals.failedCalls + (call.outcome === 'failed' ? 1 : 0),
    inputTokens: totals.inputTokens + (usage?.inputTokens ?? 0),
    cachedInputTokens:
      totals.cachedInputTokens + (usage?.cachedInputTokens ?? 0),
    cacheWriteTokens: totals.cacheWriteTokens + (usage?.cacheWriteTokens ?? 0),
    outputTokens: totals.outputTokens + (usage?.outputTokens ?? 0),
    reasoningTokens: totals.reasoningTokens + (usage?.reasoningTokens ?? 0),
    durationMs: totals.durationMs + call.durationMs,
  };
};

/** Sums {@link ProjectFileAiCall} usage per step; steps without calls are left out. */
export const summarizeProjectFileAiCallsByStep = (
  calls: readonly ProjectFileAiCall[],
): Partial<Record<ProjectFilePipelineAiStep, ProjectFileAiStepTotals>> =>
  calls.reduce<
    Partial<Record<ProjectFilePipelineAiStep, ProjectFileAiStepTotals>>
  >((acc, call) => {
    acc[call.step] = addCall(acc[call.step] ?? emptyTotals(), call);
    return acc;
  }, {});

type TokenPrices = {
  input: number;
  cachedInput: number;
  cacheWrite: number;
  output: number;
};

/**
 * List prices in USD per 1M tokens. A call above `longContextAbove` input
 * tokens is billed at the long-context prices as a whole. Models left out
 * get no estimate.
 */
const OPEN_AI_PRICES_PER_MILLION: Readonly<
  Record<
    string,
    TokenPrices & { longContextAbove: number; longContext: TokenPrices }
  >
> = {
  'gpt-6.1-sol': {
    input: 2,
    cachedInput: 0.1,
    cacheWrite: 2.5,
    output: 10,
    longContextAbove: 272_000,
    longContext: { input: 4, cachedInput: 0.2, cacheWrite: 5, output: 15 },
  },
};

/** True when the call's input is billed at the long-context prices. */
export const projectFileAiCallIsLongContext = (
  call: ProjectFileAiCall,
): boolean => {
  if (call.outcome === 'failed' || call.usage == null) return false;
  const model = OPEN_AI_PRICES_PER_MILLION[call.model];
  return model != null && call.usage.inputTokens > model.longContextAbove;
};

/** Estimated list price of one call in USD; null when the model or usage is unknown. */
export const projectFileAiCallCostUsd = (
  call: ProjectFileAiCall,
): number | null => {
  if (call.outcome === 'failed') return 0;
  const model = OPEN_AI_PRICES_PER_MILLION[call.model];
  if (model == null || call.usage == null) return null;
  const usage = call.usage;
  const prices = projectFileAiCallIsLongContext(call)
    ? model.longContext
    : model;
  const uncachedInput =
    usage.inputTokens - usage.cachedInputTokens - usage.cacheWriteTokens;
  return (
    (uncachedInput * prices.input +
      usage.cachedInputTokens * prices.cachedInput +
      usage.cacheWriteTokens * prices.cacheWrite +
      usage.outputTokens * prices.output) /
    1_000_000
  );
};

export type ProjectFileAiCost = ProjectFileAiStepTotals & {
  /** Sum over the calls with a known price. */
  usd: number;
  /** Calls left out of {@link usd}: unknown model or no usage reported. */
  unpricedCalls: number;
};

/** Token totals and estimated cost of every call in a batch. */
export const summarizeProjectFileAiCost = (
  calls: readonly ProjectFileAiCall[],
): ProjectFileAiCost =>
  calls.reduce<ProjectFileAiCost>(
    (acc, call) => {
      const usd = projectFileAiCallCostUsd(call);
      return {
        ...addCall(acc, call),
        usd: acc.usd + (usd ?? 0),
        unpricedCalls: acc.unpricedCalls + (usd === null ? 1 : 0),
      };
    },
    { ...emptyTotals(), usd: 0, unpricedCalls: 0 },
  );

export type ProjectFileAiStepCost = ProjectFileAiCost & {
  step: ProjectFilePipelineAiStep;
  /** Calls billed at the long-context prices. */
  longContextCalls: number;
};

/** {@link summarizeProjectFileAiCost} per step, most expensive step first. */
export const summarizeProjectFileAiCostByStep = (
  calls: readonly ProjectFileAiCall[],
): ProjectFileAiStepCost[] =>
  PROJECT_FILE_PIPELINE_AI_STEPS.flatMap((step) => {
    const stepCalls = calls.filter((call) => call.step === step);
    if (stepCalls.length === 0) return [];
    return [
      {
        ...summarizeProjectFileAiCost(stepCalls),
        step,
        longContextCalls: stepCalls.filter(projectFileAiCallIsLongContext)
          .length,
      },
    ];
  }).sort((a, b) => b.usd - a.usd);
